
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { Sparkles, Lock, Info } from 'lucide-react';
import { useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface JobMatchScoreProps {
  score: number;
  isPremium: boolean;
  size?: 'sm' | 'md' | 'lg';
  type?: 'job' | 'freelancer' | 'project' | 'profile';
}

export const JobMatchScore = ({ 
  score, 
  isPremium, 
  size = 'md',
  type = 'job' 
}: JobMatchScoreProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Determine color based on score
  const getColor = (value: number) => {
    if (value >= 90) return '#10b981'; // emerald-500
    if (value >= 80) return '#3b82f6'; // blue-500
    if (value >= 70) return '#f59e0b'; // amber-500
    return '#6b7280'; // gray-500
  };
  
  // Format display score - premium users see actual score, non-premium see a range
  const displayScore = isPremium ? score : `${Math.floor(score / 10) * 10}+`;

  // Determine the width and height based on size
  const getSize = () => {
    switch (size) {
      case 'sm': return 'w-7 h-7';
      case 'lg': return 'w-12 h-12';
      case 'md':
      default: return 'w-9 h-9';
    }
  };

  // Determine the icon size based on component size
  const getIconSize = () => {
    switch (size) {
      case 'sm': return 'h-3 w-3';
      case 'lg': return 'h-4 w-4';
      case 'md':
      default: return 'h-3.5 w-3.5';
    }
  };
  
  return (
    <TooltipProvider>
      <Tooltip open={showTooltip}>
        <TooltipTrigger asChild>
          <div 
            className="flex items-center gap-1.5"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <div className={getSize()}>
              <CircularProgressbar
                value={score}
                text={`${displayScore}${typeof displayScore === 'string' ? '' : '%'}`}
                styles={buildStyles({
                  textSize: size === 'sm' ? '38px' : size === 'lg' ? '28px' : '32px',
                  pathColor: getColor(score),
                  textColor: getColor(score),
                  trailColor: '#e5e7eb',
                })}
              />
            </div>
            
            {isPremium && (
              <Sparkles className={`${getIconSize()} text-amber-500`} />
            )}
            
            {!isPremium && (
              <Lock className={`${getIconSize()} text-gray-400`} />
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs">
          <div className="text-sm">
            {isPremium ? (
              <>
                <p className="font-medium">
                  {type === 'freelancer' ? 'Compatibility Score: ' : 
                   type === 'project' ? 'Project Match Score: ' :
                   type === 'profile' ? 'Profile Score: ' :
                   'Match Score: '}
                  {score}%
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {score >= 90 ? 
                    (type === 'freelancer' ? 'Excellent match for this job!' : 
                     type === 'project' ? 'Excellent match for your skills!' : 
                     type === 'profile' ? 'Excellent profile optimization!' :
                     'Excellent match for your profile!') : 
                   score >= 80 ? 
                    (type === 'freelancer' ? 'Strong match for this job' : 
                     type === 'project' ? 'Strong match for your skills' : 
                     type === 'profile' ? 'Strong profile optimization' :
                     'Strong match for your profile') : 
                   score >= 70 ? 
                    (type === 'freelancer' ? 'Good match for this job' : 
                     type === 'project' ? 'Good match for your skills' : 
                     type === 'profile' ? 'Good profile optimization' :
                     'Good match for your profile') : 
                    (type === 'freelancer' ? 'Moderate match for this job' : 
                     type === 'project' ? 'Moderate match for your skills' : 
                     type === 'profile' ? 'Moderate profile optimization' :
                     'Moderate match for your profile')}
                </p>
              </>
            ) : (
              <>
                <p className="font-medium">Limited Match View</p>
                <p className="text-xs text-gray-500 mt-1">
                  Upgrade to Premium to see exact match scores and detailed analysis.
                </p>
              </>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
